/**
 * TanStack Query Client Configuration
 * Centralized configuration for react-query
 */
import { QueryClient } from '@tanstack/react-query';

/**
 * Default query options for the application
 */
const defaultQueryOptions = {
  queries: {
    // Data is considered fresh for 5 minutes
    staleTime: 1000 * 60 * 5,
    // Cache data for 30 minutes before garbage collection
    gcTime: 1000 * 60 * 30,
    // Only retry once on failure
    retry: 1,
    // Don't refetch on window focus by default
    refetchOnWindowFocus: false,
    // Don't refetch on reconnect by default
    refetchOnReconnect: false,
  },
  mutations: {
    // Retry mutations once on failure
    retry: 1,
  },
};

/**
 * Create and export the query client
 * Created outside of React component to avoid recreation on every render
 */
export const queryClient = new QueryClient({
  defaultOptions: defaultQueryOptions,
});

/**
 * Query keys factory for consistent key management
 * This helps prevent typos and makes invalidation easier
 */
export const queryKeys = {
  // Session related queries
  sessions: {
    all: ['sessions'] as const,
    lists: () => [...queryKeys.sessions.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.sessions.lists(), filters] as const,
    details: () => [...queryKeys.sessions.all, 'detail'] as const,
    detail: (id: number | string) =>
      [...queryKeys.sessions.details(), id] as const,
    today: () => [...queryKeys.sessions.all, 'today'] as const,
  },

  // Survey related queries
  surveys: {
    all: ['surveys'] as const,
    questions: () => [...queryKeys.surveys.all, 'questions'] as const,
    question: (id: number | string) =>
      [...queryKeys.surveys.questions(), id] as const,
    answers: () => [...queryKeys.surveys.all, 'answers'] as const,
    answer: (filters: Record<string, unknown>) =>
      [...queryKeys.surveys.answers(), filters] as const,
  },

  // Masseuse related queries
  masseuses: {
    all: ['masseuses'] as const,
    lists: () => [...queryKeys.masseuses.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.masseuses.lists(), filters] as const,
    details: () => [...queryKeys.masseuses.all, 'detail'] as const,
    detail: (id: number | string) =>
      [...queryKeys.masseuses.details(), id] as const,
  },

  // Session survey results
  sessionSurveyResults: {
    all: ['sessionSurveyResults'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.sessionSurveyResults.all, filters] as const,
  },
} as const;

export default queryClient;
